import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import { mdiCalendarClockOutline } from "@mdi/js"
import { formatAddressInline } from "../../../../../../../../../../../_shared/_logic/countries"
import {
  buildPropertyObjectTagPresentation,
  type PropertyObjectTagPresentation,
} from "../../../../../../../../../_shared/_logic/object-tags"
import { buildAppointmentSubLabel } from "../../../../../../../../../../../_shared/_logic/appointment-contact"
import {
  buildAppointmentTimeTagPresentation,
  type AppointmentTimeTagInput,
} from "../../../../../../../../../../../_shared/_logic/appointment-time-tags"
import {
  buildRichTooltip,
  tooltipField,
  tooltipTitle,
} from "../../../../../../../../../../../_shared/_logic/rich-tooltips"
import {
  formatOrderTypeTagLabel,
  resolveOrderTypeColor,
} from "../../../../../../../../../../../_shared/_logic/order-type-tags"
import {
  buildWorkOrderSummaryTagPresentation,
  type WorkOrderSummaryTagPresentation,
} from "../../../../../../../../../_shared/_logic/summary-tag"

export type WorkOrderAppointmentHistoryTag = {
  status: StatusDefinition<string>
  subLabel?: string | null
  tooltip?: string | null
}

export type WorkOrderTableAppointmentPresentation = {
  status: StatusDefinition<string> | null
  subLabel?: string | null
  tooltipContent?: string | null
  historyItems?: WorkOrderAppointmentHistoryTag[]
  fallbackLabel?: string | null
}

export type WorkOrderTablePropertyPresentation = {
  tag: PropertyObjectTagPresentation
  tooltipContent: string
}

const cleanText = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const normalizeOrderNumber = (value: unknown): string => cleanText(value).replace(/^#/, "")

const parseWorkOrderLabel = (label?: string | null) => {
  const normalized = cleanText(label)
  const numberMatch = normalized.match(/#?\b(\d+)\b/)
  const dashMatch = normalized.match(/^\s*#?(\d+)\s*[-–]\s*(.+)$/)

  return {
    numberLabel: normalizeOrderNumber(dashMatch?.[1] ?? numberMatch?.[1]),
    typeLabel: cleanText(dashMatch?.[2]) || normalized.replace(/#?\b\d+\b/g, "").trim(),
  }
}

export const buildWorkOrderTableSummaryTag = ({
  workOrder,
  label,
  subLabel,
  orderNumber,
  orderTypeLabel,
  tag,
}: {
  workOrder?: WorkOrder | null
  label?: string | null
  subLabel?: string | null
  orderNumber?: string | number | null
  orderTypeLabel?: string | null
  tag?: WorkOrderSummaryTagPresentation | null
}): WorkOrderSummaryTagPresentation | null => {
  if (tag) return tag

  if (workOrder) return buildWorkOrderSummaryTagPresentation({ workOrder })

  const parsed = parseWorkOrderLabel(label)
  const fullLabel = cleanText(orderTypeLabel, parsed.typeLabel, label)

  if (!fullLabel && !parsed.numberLabel && !cleanText(orderNumber)) return null

  const numberLabel = normalizeOrderNumber(orderNumber) || parsed.numberLabel || "–"
  const resolvedFullLabel = fullLabel || "Auftrag"
  const resolvedSubLabel = cleanText(subLabel) || "fällig: –"
  const labelText = formatOrderTypeTagLabel(resolvedFullLabel)

  return {
    label: labelText,
    subLabel: resolvedSubLabel,
    color: resolveOrderTypeColor(resolvedFullLabel),
    numberLabel,
    fullLabel: resolvedFullLabel,
    sortLabel: `${labelText} ${numberLabel}`.trim(),
    dueLabel: resolvedSubLabel,
    tooltip: buildRichTooltip([
      tooltipTitle("Auftrag"),
      tooltipField("Auftragstyp", resolvedFullLabel),
      tooltipField("Auftragsnummer", numberLabel === "–" ? "" : numberLabel),
      tooltipField("Zusatz", resolvedSubLabel),
    ]),
  }
}

export const buildWorkOrderTableAppointmentTag = ({
  status,
  subLabel,
  tooltipContent,
  historyItems,
  fallbackLabel,
  scheduleLabel,
  appointmentTime,
  employeeLabel,
  contactName,
  workOrderLabel,
  subLabelOverride,
}: {
  status?: StatusDefinition<string> | null
  subLabel?: string | null
  tooltipContent?: string | null
  historyItems?: WorkOrderAppointmentHistoryTag[]
  fallbackLabel?: string | null
  scheduleLabel?: string | null
  appointmentTime?: AppointmentTimeTagInput | null
  employeeLabel?: string | null
  contactName?: string | null
  workOrderLabel?: string | null
  subLabelOverride?: string | null
}): WorkOrderTableAppointmentPresentation => {
  if (status || tooltipContent || historyItems?.length) {
    return {
      status: status ?? null,
      subLabel: subLabelOverride !== undefined ? subLabelOverride : subLabel,
      tooltipContent,
      historyItems: historyItems ?? [],
      fallbackLabel,
    }
  }

  const appointment = buildAppointmentTimeTagPresentation(appointmentTime)
  const label =
    appointmentTime && appointment.label !== "Kein Termin"
      ? appointment.label
      : cleanText(scheduleLabel) || cleanText(fallbackLabel) || "Termin"

  return {
    status: {
      value: "INBOX_APPOINTMENT",
      label,
      color: "neutral",
      icon: mdiCalendarClockOutline,
      description: "Ankündigungsstatus unbekannt.",
    },
    subLabel:
      subLabelOverride !== undefined
        ? subLabelOverride
        : buildAppointmentSubLabel({
            employeeLabel: cleanText(employeeLabel),
            contactName: cleanText(contactName),
            prefixEmployeeOnly: true,
          }),
    tooltipContent: buildRichTooltip([
      tooltipTitle("Termin"),
      tooltipField("Auftrag", workOrderLabel),
      tooltipField(
        appointment.usesCustomerWindow ? "Kalendertermin" : "Zeit",
        appointment.scheduledPeriodLabel || label,
      ),
      tooltipField(
        appointment.usesCustomerWindow ? "Kunden-Zeitfenster" : "Zeitfenster",
        appointment.timeWindowLabel,
      ),
      tooltipField("Mitarbeiter", employeeLabel),
      tooltipField("Ansprechpartner", contactName),
    ]),
    historyItems: [],
    fallbackLabel: label,
  }
}

export const buildEmptyWorkOrderTableAppointmentTag = (
  label = "Kein Termin",
): WorkOrderTableAppointmentPresentation => ({
  status: {
    value: "INBOX_NO_APPOINTMENT",
    label,
    color: "slate",
    icon: mdiCalendarClockOutline,
  },
  subLabel: null,
  tooltipContent: buildRichTooltip([tooltipTitle("Termin"), tooltipField("Status", label)]),
  historyItems: [],
  fallbackLabel: label,
})

export const buildWorkOrderTablePropertyTag = ({
  property,
  propertyUnitId,
  addressLabel,
  unitLabel,
}: {
  property?: Property | null
  propertyUnitId?: string | null
  addressLabel?: string | null
  unitLabel?: string | null
}): WorkOrderTablePropertyPresentation => {
  const tag = buildPropertyObjectTagPresentation({
    property,
    propertyUnitId,
    fallbackAddressLabel: addressLabel,
    fallbackUnitLabel: unitLabel,
    mode: "address-first",
  })

  return {
    tag,
    tooltipContent: buildRichTooltip([
      tooltipTitle("Liegenschaft"),
      tooltipField("Adresse", property ? formatAddressInline(property) : addressLabel || tag.label),
      tooltipField("Einheit", tag.subLabel || unitLabel),
    ]),
  }
}
